/**
 * Admin Operations Service - Phase 13
 * 
 * API client for Admin AI Operations endpoints.
 * All endpoints require admin-level authentication.
 */

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api/admin-ops`;
const SECURITY_API = `${BACKEND_URL}/api/admin-ops/security`;

class AdminOpsService {
  constructor() {
    this.authHeaders = {};
  }

  setAuthHeaders(headers) {
    this.authHeaders = headers;
  }

  async request(endpoint, options = {}) {
    const url = `${API}${endpoint}`;
    const fetchOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeaders,
        ...options.headers,
      },
      credentials: 'include',
    };
    
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      // For 403 errors that might need 2FA, fetch the response body fresh
      if (response.status === 403) {
        try {
          // Check for 2FA requirement via header first
          const requires2FAHeader = response.headers.get('X-Requires-2FA') === 'true';
          const action = response.headers.get('X-2FA-Action');
          
          if (requires2FAHeader) {
            const twoFaError = new Error('2FA verification required');
            twoFaError.requires2FA = true;
            twoFaError.action = action || 'unknown';
            throw twoFaError;
          }
          
          // Try to get the body from the response
          const errorData = await response.json();
          const detail = errorData.detail;
          
          if (detail && typeof detail === 'object' && detail.requires_2fa) {
            const twoFaError = new Error(detail.error || '2FA verification required');
            twoFaError.requires2FA = true;
            twoFaError.action = detail.action;
            twoFaError.targetId = detail.target_id;
            throw twoFaError;
          }
          
          // Not a 2FA error, throw regular error
          const errorMessage = typeof detail === 'object' 
            ? (detail.error || detail.message || JSON.stringify(detail))
            : (detail || `HTTP ${response.status}`);
          throw new Error(errorMessage);
        } catch (e) {
          if (e.requires2FA) throw e; // Re-throw if it's our 2FA error
          // If JSON parsing failed, throw generic 403
          throw new Error('Access denied (403)');
        }
      }
      
      // For other errors
      let error;
      try {
        error = await response.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      
      const errorMessage = typeof error.detail === 'object' 
        ? (error.detail.error || error.detail.message || JSON.stringify(error.detail))
        : (error.detail || `HTTP ${response.status}`);
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getDataimpulseLocationCatalog() {
    return this.request('/dataimpulse-location-catalog');
  }

  async importDataimpulseLocationCatalog(file) {
    if (!file) throw new Error('Choose a location catalog workbook.');
    const url = `${API}/dataimpulse-location-catalog/import`;
    const headers = {
      Accept: 'application/json',
      ...this.authHeaders,
    };
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers,
      body,
    });
    if (!response.ok) {
      let error;
      try {
        error = await response.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      const detail = error?.detail;
      const errorMessage = typeof detail === 'object'
        ? (detail.error || detail.message || 'Unable to import the location catalog.')
        : (detail || 'Unable to import the location catalog.');
      throw new Error(errorMessage);
    }
    return response.json();
  }

  async deleteDataimpulseLocationCatalog() {
    return this.request('/dataimpulse-location-catalog', { method: 'DELETE' });
  }

  async getDataimpulseProvider() {
    return this.request('/dataimpulse-provider');
  }

  async putDataimpulseProvider(body) {
    return this.request('/dataimpulse-provider', {
      method: 'PUT',
      body: JSON.stringify(body || {}),
    });
  }

  async testDataimpulseProvider() {
    return this.request('/dataimpulse-provider/test', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async rotateDataimpulseProvider(body) {
    return this.request('/dataimpulse-provider/rotate', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  async disableDataimpulseProvider() {
    return this.request('/dataimpulse-provider/disable', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async get911ProxyProvider() {
    return this.request('/911proxy-provider');
  }

  async put911ProxyProvider(body) {
    return this.request('/911proxy-provider', {
      method: 'PUT',
      body: JSON.stringify(body || {}),
    });
  }

  async test911ProxyProvider() {
    return this.request('/911proxy-provider/test', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async rotate911ProxyProvider(body) {
    return this.request('/911proxy-provider/rotate', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  async disable911ProxyProvider() {
    return this.request('/911proxy-provider/disable', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async list911ProxyIpUsage(params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
    });
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/911proxy-provider/ip-usage${suffix}`);
  }

  async get911ProxyIpUsageSummary() {
    return this.request('/911proxy-provider/ip-usage/summary');
  }

  async get911ProxyIpUsage(usageId) {
    return this.request(`/911proxy-provider/ip-usage/${encodeURIComponent(usageId)}`);
  }

  async list911ProxyIpSessions(usageId, { page = 1, pageSize = 25 } = {}) {
    return this.request(
      `/911proxy-provider/ip-usage/${encodeURIComponent(usageId)}/sessions?page=${page}&page_size=${pageSize}`,
    );
  }

  async search911ProxyIp(ip) {
    return this.request('/911proxy-provider/ip-usage/search-ip', {
      method: 'POST',
      body: JSON.stringify({ ip }),
    });
  }

  async reveal911ProxyIp(usageId) {
    return this.request(`/911proxy-provider/ip-usage/${encodeURIComponent(usageId)}/reveal`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async copy911ProxyIp(usageId) {
    return this.request(`/911proxy-provider/ip-usage/${encodeURIComponent(usageId)}/copy`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Provider-aware IP Usage (shared by 911Proxy and DataImpulse).
  async listProviderIpUsage(provider, params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
    });
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/data-providers/${encodeURIComponent(provider)}/ip-usage${suffix}`);
  }

  async getProviderIpUsageSummary(provider) {
    return this.request(`/data-providers/${encodeURIComponent(provider)}/ip-usage/summary`);
  }

  async getProviderIpUsage(provider, usageId) {
    return this.request(`/data-providers/${encodeURIComponent(provider)}/ip-usage/${encodeURIComponent(usageId)}`);
  }

  async listProviderIpSessions(provider, usageId, { page = 1, pageSize = 25 } = {}) {
    return this.request(
      `/data-providers/${encodeURIComponent(provider)}/ip-usage/${encodeURIComponent(usageId)}/sessions?page=${page}&page_size=${pageSize}`,
    );
  }

  async searchProviderIp(provider, ip) {
    return this.request(`/data-providers/${encodeURIComponent(provider)}/ip-usage/search-ip`, {
      method: 'POST',
      body: JSON.stringify({ ip }),
    });
  }

  async revealProviderIp(provider, usageId) {
    return this.request(`/data-providers/${encodeURIComponent(provider)}/ip-usage/${encodeURIComponent(usageId)}/reveal`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async copyProviderIp(provider, usageId) {
    return this.request(`/data-providers/${encodeURIComponent(provider)}/ip-usage/${encodeURIComponent(usageId)}/copy`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async cleanupDataimpulseLegacyClientSecrets() {
    return this.request('/dataimpulse-provider/cleanup-legacy-client-secrets', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async getQaProxyPolicy() {
    return this.request('/qa-proxy-policy');
  }

  async putQaProxyPolicy(body) {
    return this.request('/qa-proxy-policy', {
      method: 'PUT',
      body: JSON.stringify(body || {}),
    });
  }

  async getQaWebsiteAccess(params = {}) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) search.set(key, value);
    });
    const suffix = search.toString() ? `?${search.toString()}` : '';
    return this.request(`/qa-website-access${suffix}`);
  }

  async getQaWebsiteAccessSummary() {
    return this.request('/qa-website-access/summary');
  }

  async analyzeQaWebsiteAccess(url) {
    return this.request('/qa-website-access/analyze', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  async createQaWebsiteAccess(payload) {
    return this.request('/qa-website-access', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async patchQaWebsiteAccess(id, payload) {
    return this.request(`/qa-website-access/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async disableQaWebsiteAccess(id) {
    return this.request(`/qa-website-access/${id}/disable`, { method: 'POST' });
  }

  async enableQaWebsiteAccess(id) {
    return this.request(`/qa-website-access/${id}/enable`, { method: 'POST' });
  }

  async deleteQaWebsiteAccess(id) {
    return this.request(`/qa-website-access/${id}`, { method: 'DELETE' });
  }

  async getQaWebsiteAccessAudit(id) {
    return this.request(`/qa-website-access/${id}/audit`);
  }

  async getQaWebsiteAccessUsage() {
    return this.request('/qa-website-access/usage');
  }

  async getQaWebsiteAccessApprovalMode() {
    return this.request('/qa-website-access/approval-mode');
  }

  async putQaWebsiteAccessApprovalMode(approvalMode) {
    return this.request('/qa-website-access/approval-mode', {
      method: 'PUT',
      body: JSON.stringify({ approvalMode }),
    });
  }

  async getQaDiagnosticsAssignments() {
    return this.request('/qa-website-access/diagnostics/assignments');
  }

  async listQaDiagnosticsRuns(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const query = params.toString();
    return this.request(`/qa-website-access/diagnostics/runs${query ? `?${query}` : ''}`);
  }

  async startQaDiagnosticsRun(assignmentId) {
    return this.request('/qa-website-access/diagnostics/runs', {
      method: 'POST',
      body: JSON.stringify({ assignmentId: assignmentId || null }),
    });
  }

  async getQaDiagnosticsRunStatus(runId) {
    return this.request(`/qa-website-access/diagnostics/runs/${runId}/status`);
  }

  async getQaDiagnosticsReport(runId) {
    return this.request(`/qa-website-access/diagnostics/runs/${runId}`);
  }

  async cancelQaDiagnosticsRun(runId) {
    return this.request(`/qa-website-access/diagnostics/runs/${runId}/cancel`, { method: 'POST' });
  }

  async retryQaDiagnosticsRun(runId) {
    return this.request(`/qa-website-access/diagnostics/runs/${runId}/retry`, { method: 'POST' });
  }

  async securityRequest(endpoint, options = {}) {
    const url = `${SECURITY_API}${endpoint}`;
    const fetchOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeaders,
        ...options.headers,
      },
      credentials: 'include',
    };
    
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      let error;
      try {
        error = await response.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // ============================================
  // 2FA MANAGEMENT (Full Enrollment Flow)
  // ============================================

  async get2FAStatus() {
    return this.securityRequest('/2fa/status');
  }

  async setup2FA(authMethod = 'totp') {
    return this.securityRequest('/2fa/setup', {
      method: 'POST',
      body: JSON.stringify({ auth_method: authMethod }),
    });
  }

  async enable2FA(code) {
    return this.securityRequest('/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async disable2FA(code, reason = null) {
    return this.securityRequest('/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ code, reason }),
    });
  }

  async regenerateBackupCodes(code) {
    return this.securityRequest('/2fa/regenerate-backup-codes', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async verify2FAForAction(code, action, targetId = null, reason = null) {
    return this.securityRequest('/2fa/verify-action', {
      method: 'POST',
      body: JSON.stringify({ code, action, target_id: targetId, reason }),
    });
  }

  // Legacy endpoint for step-up verification
  async verify2FA(code, action, targetId = null) {
    return this.request('/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ code, action, target_id: targetId }),
    });
  }

  // ============================================
  // OVERVIEW
  // ============================================

  async getOverview() {
    return this.request('/overview');
  }

  async getSystemStatus() {
    return this.request('/system-status');
  }

  // ============================================
  // WORKFLOW RULES
  // ============================================

  async listWorkflowRules(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/workflow-rules${query ? `?${query}` : ''}`);
  }

  async getWorkflowRule(ruleId) {
    return this.request(`/workflow-rules/${ruleId}`);
  }

  async createWorkflowRule(rule) {
    return this.request('/workflow-rules', {
      method: 'POST',
      body: JSON.stringify(rule),
    });
  }

  async updateWorkflowRule(ruleId, updates) {
    return this.request(`/workflow-rules/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteWorkflowRule(ruleId) {
    return this.request(`/workflow-rules/${ruleId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // METHOD LIBRARY
  // ============================================

  async listMethods(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/methods${query ? `?${query}` : ''}`);
  }

  async getMethod(methodId) {
    return this.request(`/methods/${methodId}`);
  }

  async createMethod(method) {
    return this.request('/methods', {
      method: 'POST',
      body: JSON.stringify(method),
    });
  }

  async updateMethod(methodId, updates) {
    return this.request(`/methods/${methodId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // ============================================
  // TEMPLATE MANAGER
  // ============================================

  async listTemplates(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/templates${query ? `?${query}` : ''}`);
  }

  // Alias for compatibility
  async getTemplates(params = {}) {
    return this.listTemplates(params);
  }

  async getTemplate(templateId) {
    return this.request(`/templates/${templateId}`);
  }

  async createTemplate(template) {
    return this.request('/templates', {
      method: 'POST',
      body: JSON.stringify(template),
    });
  }

  async updateTemplate(templateId, updates) {
    return this.request(`/templates/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteTemplate(templateId) {
    const url = `${API}/templates/${templateId}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeaders,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { detail: `HTTP ${response.status}` };
      }
      
      // Extract the error message from FastAPI's detail field
      const errorMessage = typeof errorData.detail === 'string' 
        ? errorData.detail
        : (errorData.detail?.error || errorData.detail?.message || `HTTP ${response.status}`);
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.response = { data: errorData };
      throw error;
    }

    return response.json();
  }

  // ============================================
  // REVIEW QUEUE
  // ============================================

  async listReviewQueue(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/review-queue${query ? `?${query}` : ''}`);
  }

  async createReviewItem(item) {
    return this.request('/review-queue', {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateReviewItem(itemId, updates) {
    return this.request(`/review-queue/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // ============================================
  // PROMPTS
  // ============================================

  async listPrompts(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/prompts${query ? `?${query}` : ''}`);
  }

  async createPrompt(prompt) {
    return this.request('/prompts', {
      method: 'POST',
      body: JSON.stringify(prompt),
    });
  }

  async updatePrompt(promptId, updates) {
    return this.request(`/prompts/${promptId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async activatePrompt(promptId) {
    return this.request(`/prompts/${promptId}/activate`, {
      method: 'PUT',
    });
  }

  async deletePrompt(promptId) {
    return this.request(`/prompts/${promptId}`, {
      method: 'DELETE',
    });
  }

  async deleteLivePrompt(promptId, reason, twoFAToken = null, replacementPromptId = null) {
    const url = `${API}/prompts/${promptId}/live`;
    const headers = {
      'Content-Type': 'application/json',
      ...this.authHeaders,
    };
    if (twoFAToken) {
      headers['X-2FA-Token'] = twoFAToken;
    }
    
    // Use XMLHttpRequest to bypass middleware that consumes response body
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('DELETE', url, true);
      
      // Set headers
      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value);
      }
      xhr.withCredentials = true;
      
      xhr.onload = function() {
        let responseData = null;
        try {
          responseData = JSON.parse(xhr.responseText);
        } catch {
          // Not JSON
        }
        
        const detail = responseData?.detail;
        
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(responseData);
          return;
        }
        
        // Case 1: 403 with X-Requires-2FA header = 2FA verification required
        if (xhr.status === 403) {
          const requires2FA = xhr.getResponseHeader('X-Requires-2FA') === 'true';
          
          if (requires2FA) {
            const twoFaError = new Error('2FA verification required');
            twoFaError.requires2FA = true;
            twoFaError.action = xhr.getResponseHeader('X-2FA-Action') || 'prompt_delete';
            twoFaError.targetId = promptId;
            twoFaError.reasonRequired = true;
            reject(twoFaError);
            return;
          }
          
          reject(new Error(detail?.error || detail || 'Access denied'));
          return;
        }
        
        // Case 2: 400 with safety_block = Safety block error
        if (xhr.status === 400 && detail && typeof detail === 'object' && detail.safety_block) {
          const safetyError = new Error(detail.error || 'Action blocked by safety rule');
          safetyError.safetyBlock = true;
          safetyError.taskType = detail.task_type;
          safetyError.suggestion = detail.suggestion;
          reject(safetyError);
          return;
        }
        
        // Case 3: Other errors
        let errorMessage;
        if (typeof detail === 'object') {
          errorMessage = detail.error || detail.message || JSON.stringify(detail);
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (xhr.responseText) {
          errorMessage = xhr.responseText.substring(0, 200);
        } else {
          errorMessage = `HTTP ${xhr.status}`;
        }
        
        reject(new Error(errorMessage));
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      xhr.send(JSON.stringify({ 
        reason,
        replacement_prompt_id: replacementPromptId 
      }));
    });
  }

  // ============================================
  // SANDBOX TESTS
  // ============================================

  async listSandboxTests(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/sandbox-tests${query ? `?${query}` : ''}`);
  }

  async createSandboxTest(test) {
    return this.request('/sandbox-tests', {
      method: 'POST',
      body: JSON.stringify(test),
    });
  }

  async runSandboxTest(testId) {
    return this.request(`/sandbox-tests/${testId}/run`, {
      method: 'POST',
    });
  }

  // ============================================
  // KNOWLEDGE BASE
  // ============================================

  async listKnowledgeEntries(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/knowledge-base${query ? `?${query}` : ''}`);
  }

  async createKnowledgeEntry(entry) {
    return this.request('/knowledge-base', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  }

  async updateKnowledgeEntry(entryId, updates) {
    return this.request(`/knowledge-base/${entryId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // ============================================
  // LETTER EXAMPLES
  // ============================================

  async listLetterExamples(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/letter-examples${query ? `?${query}` : ''}`);
  }

  async createLetterExample(example) {
    return this.request('/letter-examples', {
      method: 'POST',
      body: JSON.stringify(example),
    });
  }

  async updateLetterExample(exampleId, updates) {
    return this.request(`/letter-examples/${exampleId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteLetterExample(exampleId) {
    return this.request(`/letter-examples/${exampleId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // PARSER CORRECTIONS
  // ============================================

  async listParserCorrections(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/parser-corrections${query ? `?${query}` : ''}`);
  }

  async createParserCorrection(correction) {
    return this.request('/parser-corrections', {
      method: 'POST',
      body: JSON.stringify(correction),
    });
  }

  // ============================================
  // PERMISSIONS
  // ============================================

  async listPermissions() {
    return this.request('/permissions');
  }

  async grantPermission(permission) {
    return this.request('/permissions', {
      method: 'POST',
      body: JSON.stringify(permission),
    });
  }

  // ============================================
  // TRAINING INTAKE
  // ============================================

  async listTrainingIntake(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/training-intake${query ? `?${query}` : ''}`);
  }

  async getTrainingIntakeItem(itemId) {
    return this.request(`/training-intake/${itemId}`);
  }

  async createTrainingIntake(item) {
    return this.request('/training-intake', {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async createBatchTrainingIntake(items) {
    return this.request('/training-intake/batch', {
      method: 'POST',
      body: JSON.stringify(items),
    });
  }

  async updateTrainingIntake(itemId, updates) {
    return this.request(`/training-intake/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async promoteTrainingIntake(itemId, twoFAToken = null, notes = null) {
    const url = `${API}/training-intake/${itemId}/promote`;
    const headers = {
      'Content-Type': 'application/json',
      ...this.authHeaders,
    };
    if (twoFAToken) {
      headers['X-2FA-Token'] = twoFAToken;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ promotion_notes: notes }),
    });

    // Check for 2FA requirement via header (works even if body stream is consumed)
    if (response.status === 403) {
      const requires2FA = response.headers.get('X-Requires-2FA') === 'true';
      const action = response.headers.get('X-2FA-Action');
      
      if (requires2FA) {
        const twoFaError = new Error('2FA verification required');
        twoFaError.requires2FA = true;
        twoFaError.action = action || 'intake_promote';
        twoFaError.targetId = itemId;
        throw twoFaError;
      }
      
      // Not a 2FA error, try to get error message
      try {
        const errorData = await response.json();
        throw new Error(errorData.detail?.error || errorData.detail || 'Access denied');
      } catch (e) {
        if (e.requires2FA) throw e;
        throw new Error('Access denied (403)');
      }
    }

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { detail: `HTTP ${response.status}` };
      }
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async archiveTrainingIntake(itemId, reason = null) {
    const body = reason ? JSON.stringify({ reason }) : JSON.stringify({});
    return this.request(`/training-intake/${itemId}`, {
      method: 'DELETE',
      body: body,
    });
  }

  async restoreTrainingIntake(itemId) {
    return this.request(`/training-intake/${itemId}/restore`, {
      method: 'POST',
    });
  }

  async duplicateTrainingIntake(itemId, title = null) {
    return this.request(`/training-intake/${itemId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  }

  async getTrainingIntakeDeleteInfo(itemId) {
    return this.request(`/training-intake/${itemId}/delete-info`);
  }

  async deleteTrainingIntake(itemId, options = {}) {
    const { reason, deleteLinked = false, force = false, twoFAToken = null } = options;
    
    const url = `${API}/training-intake/${itemId}/permanent`;
    const headers = {
      'Content-Type': 'application/json',
      ...this.authHeaders,
    };
    
    if (twoFAToken) {
      headers['X-2FA-Token'] = twoFAToken;
    }
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      credentials: 'include',
      body: JSON.stringify({ 
        reason,
        delete_linked: deleteLinked,
        force 
      }),
    });

    // Check for 2FA requirement via header
    if (response.status === 403) {
      const requires2FA = response.headers.get('X-Requires-2FA') === 'true';
      const action = response.headers.get('X-2FA-Action');
      
      if (requires2FA) {
        const twoFaError = new Error('2FA verification required');
        twoFaError.requires2FA = true;
        twoFaError.action = action || 'intake_delete';
        twoFaError.targetId = itemId;
        throw twoFaError;
      }
      
      try {
        const errorData = await response.json();
        const detail = errorData.detail;
        if (detail && typeof detail === 'object' && detail.requires_2fa) {
          const twoFaError = new Error(detail.error || '2FA verification required');
          twoFaError.requires2FA = true;
          twoFaError.action = detail.action;
          twoFaError.targetId = itemId;
          twoFaError.riskLevel = detail.risk_level;
          throw twoFaError;
        }
        throw new Error(detail?.error || detail || 'Access denied');
      } catch (e) {
        if (e.requires2FA) throw e;
        throw new Error('Access denied (403)');
      }
    }

    if (response.status === 400) {
      try {
        const errorData = await response.json();
        const detail = errorData.detail;
        if (detail && typeof detail === 'object') {
          if (detail.requires_force) {
            const forceError = new Error(detail.error);
            forceError.requiresForce = true;
            forceError.hasLinkedData = detail.has_linked_data;
            forceError.linkedEntries = detail.linked_entries;
            throw forceError;
          }
          if (detail.requires_reason) {
            const reasonError = new Error(detail.error);
            reasonError.requiresReason = true;
            throw reasonError;
          }
          throw new Error(detail.error || JSON.stringify(detail));
        }
        throw new Error(detail || 'Bad request');
      } catch (e) {
        if (e.requiresForce || e.requiresReason) throw e;
        throw e;
      }
    }

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { detail: `HTTP ${response.status}` };
      }
      throw new Error(errorData.detail?.error || errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // ============================================
  // AI ASSIST
  // ============================================

  async runAIAssist(content, title, currentContentType, currentDestination, enabledTasks) {
    return this.request('/training-intake/ai-assist', {
      method: 'POST',
      body: JSON.stringify({
        content,
        title,
        current_content_type: currentContentType,
        current_destination: currentDestination,
        enabled_tasks: enabledTasks,
      }),
    });
  }

  async runDeepAnalysis(content, title) {
    return this.request('/training-intake/deep-analysis', {
      method: 'POST',
      body: JSON.stringify({ content, title }),
    });
  }

  async pollDeepAnalysisStatus(jobId) {
    return this.request(`/training-intake/deep-analysis/status/${jobId}`, {
      method: 'GET',
    });
  }

  /**
   * Run deep analysis with automatic async job handling for long content.
   * Polls for completion if the backend returns an async job.
   * @param {string} content - The content to analyze
   * @param {string} title - The title of the content
   * @param {function} onProgress - Optional callback for progress updates (stage, progress)
   * @returns {Promise<object>} - The analysis result
   */
  async runDeepAnalysisWithPolling(content, title, onProgress = null) {
    const initialResponse = await this.runDeepAnalysis(content, title);
    
    // If it's an async job, poll for completion
    if (initialResponse.async_job && initialResponse.job_id) {
      const jobId = initialResponse.job_id;
      const maxPollTime = 180000; // 3 minutes max
      const pollInterval = 2000; // Poll every 2 seconds
      const startTime = Date.now();
      
      if (onProgress) {
        onProgress('initializing', 5);
      }
      
      while (Date.now() - startTime < maxPollTime) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        
        try {
          const status = await this.pollDeepAnalysisStatus(jobId);
          
          if (onProgress) {
            onProgress(status.stage, status.progress);
          }
          
          if (status.status === 'complete') {
            return status.result;
          }
          
          if (status.status === 'failed') {
            // Return the error result
            return status.result || {
              success: false,
              error_message: status.error || 'Analysis job failed',
              error_type: 'job_failed',
              retry_available: true,
              fallback_available: true
            };
          }
        } catch (pollError) {
          console.error('Poll error:', pollError);
          // Continue polling on transient errors
        }
      }
      
      // Timeout waiting for job
      return {
        success: false,
        error_message: 'Analysis is taking longer than expected. Please retry or use standard analysis.',
        error_type: 'poll_timeout',
        retry_available: true,
        fallback_available: true
      };
    }
    
    // Synchronous response - return directly
    return initialResponse;
  }

  async createDownstreamElements(itemId, sectionsToCreate = null, createAllAccepted = true) {
    return this.request(`/training-intake/${itemId}/create-downstream`, {
      method: 'POST',
      body: JSON.stringify({
        sections_to_create: sectionsToCreate,
        create_all_accepted: createAllAccepted,
      }),
    });
  }

  // ============= AI DECISION ENGINE =============

  /**
   * Evaluate a dispute package using the AI decision engine.
   * Returns signals, warnings, method recommendations, and strategy.
   * 
   * @param {Object} input - Decision input
   * @param {string} input.report_id - Optional report ID
   * @param {Array} input.selected_items - Selected items to evaluate
   * @param {Object} input.user_context - User context
   * @param {Object} input.package_context - Package context
   * @returns {Promise<Object>} Decision output
   */
  async evaluateDecision(input) {
    return this.request('/decision/evaluate', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /**
   * Get decision trace for admin debugging.
   * @param {Object} input - Decision input
   * @returns {Promise<Object>} Decision trace
   */
  async getDecisionTrace(input) {
    return this.request('/decision/trace', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /**
   * Get decision audit log.
   * @param {Object} params - Query params
   * @returns {Promise<Object>} Audit log entries
   */
  async getDecisionAuditLog(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/decision/audit-log?${queryString}`);
  }

  // ============= EXTRACTION RULES =============

  async getExtractionRules(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/extraction-rules?${queryString}`);
  }

  async createExtractionRule(ruleData) {
    return this.request('/extraction-rules', {
      method: 'POST',
      body: JSON.stringify(ruleData),
    });
  }

  async updateExtractionRule(ruleId, updates) {
    return this.request(`/extraction-rules/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteExtractionRule(ruleId) {
    return this.request(`/extraction-rules/${ruleId}`, {
      method: 'DELETE',
    });
  }

  // ============= ROUTING RULES =============

  async getRoutingRules(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/routing-rules?${queryString}`);
  }

  async createRoutingRule(ruleData) {
    return this.request('/routing-rules', {
      method: 'POST',
      body: JSON.stringify(ruleData),
    });
  }

  async updateRoutingRule(ruleId, updates) {
    return this.request(`/routing-rules/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // ============= STRATEGY MAPPINGS =============

  async getStrategyMappings(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/strategy-mappings?${queryString}`);
  }

  async createStrategyMapping(mappingData) {
    return this.request('/strategy-mappings', {
      method: 'POST',
      body: JSON.stringify(mappingData),
    });
  }

  async updateStrategyMapping(mappingId, updates) {
    return this.request(`/strategy-mappings/${mappingId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }
}

// Singleton instance
const adminOpsService = new AdminOpsService();
export default adminOpsService;
